/**
 * Founder Control Center v2 · Operations / Security · Event Log.
 *
 * Vista de trazabilidad de eventos de auditoría platform-wide.
 *
 * Originalmente vivía como función interna `AuditLogTab` dentro de
 * `SuperAdmin.jsx`. Extraído en Sprint Q · Fase 7 a este módulo independiente
 * para:
 *   - eliminar la dependencia de SecuritySection con el wrapper SuperAdmin,
 *   - ofrecer un único punto de verdad (legacy /admin?tab=audit + /founder/security?sub=event-log
 *     ahora comparten exactamente el mismo componente),
 *   - simplificar futuros refactors (testabilidad y bundle splitting).
 *
 * Endpoint consumido (existente · 0 backend tocado · 0 mocks):
 *   GET /api/superadmin/audit-events?limit&action&actor_email&only_failures
 *
 * data-testid público: `audit-log-tab` (idéntico al embed legacy para no romper
 * tests existentes ni deep-links de QA).
 */
import { useEffect, useState } from "react";
import { ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatErr } from "../../lib/api";

export default function EventLog() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ action: "", actor_email: "", only_failures: false });

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("limit", "100");
      if (filter.action) params.set("action", filter.action);
      if (filter.actor_email) params.set("actor_email", filter.actor_email);
      if (filter.only_failures) params.set("only_failures", "true");
      const { data } = await api.get(`/superadmin/audit-events?${params.toString()}`);
      setData(data);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-5" data-testid="audit-log-tab">
      <div className="ag-card p-5">
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck size={18} weight="duotone" className="text-[var(--ag-gold)]" />
          <h3 className="font-display text-xl font-bold tracking-tighter">Event Log</h3>
        </div>
        <p className="text-xs text-[var(--ag-text-dim)] mb-4">
          Trazabilidad de acciones sensibles: login, logout, fallos, brute-force lockouts,
          cambios de contraseña/rol, exports fiscales, refunds. Útil para auditorías RGPD / AEAT.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
          <input type="text" placeholder="Action (ej: auth.login.fail)" value={filter.action}
            onChange={(e) => setFilter((f) => ({ ...f, action: e.target.value }))}
            className="h-9 px-2 rounded-sm bg-[var(--ag-midnight)] border border-[var(--ag-border)] text-[var(--ag-text)] text-sm"
            data-testid="audit-filter-action" />
          <input type="text" placeholder="Actor email" value={filter.actor_email}
            onChange={(e) => setFilter((f) => ({ ...f, actor_email: e.target.value }))}
            className="h-9 px-2 rounded-sm bg-[var(--ag-midnight)] border border-[var(--ag-border)] text-[var(--ag-text)] text-sm"
            data-testid="audit-filter-email" />
          <label className="text-xs flex items-center gap-2 cursor-pointer text-[var(--ag-text)]">
            <input type="checkbox" checked={filter.only_failures}
              onChange={(e) => setFilter((f) => ({ ...f, only_failures: e.target.checked }))}
              data-testid="audit-filter-failures" />
            Sólo fallos
          </label>
          <button onClick={load} className="h-9 px-4 rounded-sm bg-amber-400 hover:bg-amber-500 text-[#1A1A1A] font-semibold text-sm" data-testid="audit-apply">
            Filtrar
          </button>
        </div>

        {data && (
          <div className="mt-4 flex flex-wrap gap-3 text-xs">
            <span className="text-[var(--ag-text-dim)]">Total: <b className="text-[var(--ag-text)] tabular-nums">{data.total}</b></span>
            {data.breakdown_top10?.slice(0, 5).map((b) => (
              <span key={b.action} className="px-2 py-0.5 rounded-sm bg-[var(--ag-midnight)] border border-[var(--ag-border)] font-mono text-[10px]">
                {b.action} · {b.count}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="ag-card overflow-x-auto" data-testid="audit-list">
        {loading && <div className="p-6 text-sm text-[var(--ag-text-dim)]">Cargando…</div>}
        {!loading && data?.items?.length === 0 && (
          <div className="p-6 text-sm text-[var(--ag-text-dim)] text-center">Sin eventos para los filtros aplicados.</div>
        )}
        {!loading && data?.items?.length > 0 && (
          <table className="w-full text-xs">
            <thead className="bg-[var(--ag-midnight)] text-[var(--ag-text-dim)] text-[10px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-3 py-2">Hora</th>
                <th className="text-left px-3 py-2">Acción</th>
                <th className="text-left px-3 py-2">Actor</th>
                <th className="text-left px-3 py-2">IP</th>
                <th className="text-left px-3 py-2">Resultado</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((e) => {
                const dt = new Date(e.ts_iso);
                return (
                  <tr key={e.id} className="border-t border-[var(--ag-border)] text-[var(--ag-text)]" data-testid={`audit-row-${e.id}`}>
                    <td className="px-3 py-2 font-mono tabular-nums text-[10px] text-[var(--ag-text-dim)]">
                      {dt.toLocaleString("es-ES", { dateStyle: "short", timeStyle: "medium" })}
                    </td>
                    <td className="px-3 py-2 font-mono text-[11px]">{e.action}</td>
                    <td className="px-3 py-2 truncate max-w-[200px]">{e.actor_email || "—"}</td>
                    <td className="px-3 py-2 font-mono text-[10px] text-[var(--ag-text-dim)]">{e.ip || "—"}</td>
                    <td className="px-3 py-2">
                      {e.ok ? (
                        <span className="text-emerald-400">✓ OK</span>
                      ) : (
                        <span className="text-red-400">✗ FAIL</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
